/**
 * 🧱 Refactor BT Phase 3 — Étape 2 : section « SectionDatesChantier » d'EditerBonTravailDialog.jsx.
 * JSX déplacé TEL QUEL (ex-L1667-1801, dédenté de 10) ; variables lues via `useBTForm()`
 * (BTFormProvider enrichi par `extra` dans le composant parent). DOM, data-testid, classes, textes inchangés.
 */
import { AlertTriangle, Calendar, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { calculerHeuresBudgetees } from '../../utils';
import { getCCQInfo } from '@/lib/ccq-holidays';
import { useBTForm } from '../../BTFormContext';

export function SectionDatesChantier() {
  const {
    calDays,
    calMonth,
    congesEquipe,
    employees,
    employesEffectifsIds,
    equipeOccupee,
    form,
    lockedInfo,
    membresExclus,
    membresExtras,
    monthNames,
    setCalMonth,
    toggleDate,
    type,
  } = useBTForm();
  return (
    <>
      {/* Dates de chantier */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Calendar className="w-4 h-4" /> Dates de chantier</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center justify-between mb-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() - 1))}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="font-medium">{monthNames[calMonth.getMonth()]} {calMonth.getFullYear()}</span>
            <Button type="button" variant="ghost" size="sm" onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() + 1))}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          <div className="overflow-x-auto -mx-2">
            <div className="grid grid-cols-7 gap-1 text-center text-xs mb-1 min-w-[250px] px-2">
            {['D', 'L', 'M', 'M', 'J', 'V', 'S'].map((d, i) => <div key={i} className="text-gray-500 font-medium py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1 min-w-[250px] px-2">
            {calDays.map((day, i) => {
              if (!day) return <div key={i} />;
              const dateStr = day.toISOString().split('T')[0];
              const isSelected = form.dates_chantier.includes(dateStr);
              const isWeekend = day.getDay() === 0 || day.getDay() === 6;
              const occupeClient = equipeOccupee[dateStr];
              const isOccupe = !!occupeClient && !isSelected;
              const congeNom = congesEquipe[dateStr];
              const isConge = !!congeNom;
              // Vérifier jours fériés CCQ
              const ccqInfo = getCCQInfo ? getCCQInfo(dateStr) : null;
              const isFerie = !!(ccqInfo?.jourFerie || ccqInfo?.conge);
              const nomFerie = ccqInfo?.jourFerie?.nom || ccqInfo?.conge?.nom || '';
              // 🆕 [v9.3 — 2026-06-12] Les jours fériés CCQ s'alignent désormais
              // sur le comportement des weekends : VISUELLEMENT distincts (fond
              // rose pâle pointillé) mais CLIQUABLES pour permettre la cédulation
              // d'un BT un jour férié si la propriétaire le décide. Demande
              // explicite d'Eliot : "tu fais comme les fins de semaine, ne force
              // pas le BT dedans pour rien". Plus aucune date n'est bloquée par
              // le sélecteur (sauf `isDateLocked` qui reste pour les dates
              // verrouillées par des heures déjà enregistrées).
              const isBlocked = false;
              // 🔒 2026-05-14 — Cette date a-t-elle des heures enregistrées ?
              const isDateLocked = (lockedInfo?.locked_dates || []).includes(dateStr);
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => !isBlocked && toggleDate(dateStr)}
                  disabled={isBlocked}
                  title={
                    isDateLocked
                      ? '🔒 Heures enregistrées — date verrouillée (impossible à retirer)'
                      : isFerie ? `${nomFerie || 'Congé CCQ'} — cliquez pour céduler quand même`
                      : isConge ? `Congé: ${congeNom}`
                      : isWeekend ? 'Weekend — cliquez pour céduler quand même'
                      : occupeClient ? `Occupé: ${occupeClient}` : ''
                  }
                  className={`p-2 text-sm rounded transition-all relative ${
                    isSelected
                      ? (isDateLocked
                          ? 'bg-blue-600 text-white ring-2 ring-amber-400'
                          : isFerie
                            ? 'bg-rose-600 text-white ring-2 ring-rose-300'
                            : isWeekend
                              ? 'bg-amber-500 text-white ring-2 ring-amber-300'
                              : 'bg-blue-600 text-white')
                      : isFerie
                        ? 'bg-rose-50 text-rose-700 hover:bg-rose-100 border border-dashed border-rose-300'
                        : isConge
                          ? 'bg-amber-100 text-amber-600'
                          : isWeekend
                            ? 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-dashed border-amber-300'
                            : isOccupe
                              ? 'bg-red-50 text-red-700 ring-1 ring-red-300 hover:bg-red-100'
                              : 'hover:bg-gray-100'
                  }`}
                  data-testid={isFerie ? `cal-ferie-${dateStr}` : isWeekend ? `cal-weekend-${dateStr}` : `cal-day-${dateStr}`}
                >
                  {day.getDate()}
                  {isDateLocked && (
                    <span className="absolute top-0 right-0.5 text-[9px]" title="Heures enregistrées">🔒</span>
                  )}
                  {isOccupe && <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-red-500" />}
                </button>
              );
            })}
          </div>
          </div>
          {/* Légende disponibilité */}
          {form.chef_equipe_id && Object.keys(equipeOccupee).length > 0 && (
            <div className="mt-2 flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" /> Équipe déjà assignée</span>
            </div>
          )}
          <p className="text-xs text-gray-500 mt-1">{form.dates_chantier.length} jour(s) sélectionné(s)</p>
          {/* 🆕 2026-05-08 — Indicateur weekend en direct */}
          {(() => {
            const we = (form.dates_chantier || []).filter(d => {
              const day = new Date(d + 'T12:00:00').getDay();
              return day === 0 || day === 6;
            });
            if (we.length === 0) return null;
            return (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1 inline-flex items-center gap-1.5" data-testid="alert-weekend-inline">
                <span>🟡</span>
                <span><b>{we.length}</b> jour(s) de weekend sélectionné(s) — pensez aux taux temps double / prime CCQ</span>
              </p>
            );
          })()}
          {/* Alerte jours planifiés vs estimation */}
          {(() => {
            const nbJoursPlanifies = form.dates_chantier.length;
            const heuresBudg = parseFloat(form.heures_budgetees) || 0;
            if (nbJoursPlanifies === 0 || heuresBudg === 0) return null;
            const hpj = calculerHeuresBudgetees(1, 1, 1, form.dates_chantier).heuresParJour;
            // Nombre de membres actifs dans l'équipe (chef + membres effectifs - exclus + extras)
            // 🐛 [Phase v13.46 — 2026-07-14] Basé sur employesEffectifsIds (source de vérité v13.46).
            const membres = employees.filter(e => employesEffectifsIds.has(e.id) && e.id !== form.chef_equipe_id && e.est_actif !== false && !membresExclus.has(e.id));
            const nbMembres = (form.chef_equipe_id ? 1 : 0) + membres.length + membresExtras.length;
            const joursEstimes = nbMembres > 0 ? Math.ceil((heuresBudg / (hpj * nbMembres)) * 10) / 10 : 0;
            const diff = nbJoursPlanifies - joursEstimes;
            if (Math.abs(diff) < 0.5) return null;
            const manque = diff < 0;
            return (
              <div className={`mt-2 flex items-start gap-2 p-2.5 rounded-lg border text-xs ${manque ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-blue-50 border-blue-200 text-blue-800'}`} data-testid="alert-jours-mismatch-edit">
                <AlertTriangle className={`w-4 h-4 shrink-0 mt-0.5 ${manque ? 'text-amber-500' : 'text-blue-500'}`} />
                <span>
                  <strong>{nbJoursPlanifies} jour(s) planifie(s)</strong> mais estimation de <strong>{joursEstimes} jour(s)</strong> ({nbMembres} employes)
                  {manque ? ` — il manque ~${Math.abs(diff).toFixed(1)} jour(s) !` : ` — vous avez ~${diff.toFixed(1)} jour(s) de marge.`}
                </span>
              </div>
            );
          })()}
        </CardContent>
      </Card>

    </>
  );
}
